/**
 * Predictive Search Component
 * Provides live search suggestions as users type in the search field
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search-results]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.form = this.querySelector('form');
    this.abortController = null;
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.input) return;

    this.input.addEventListener(
      'input',
      this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length > 0) {
        this.open();
      }
    });

    this.clearButton?.addEventListener('click', () => {
      this.input.value = '';
      this.close();
      this.input.focus();
      // Hide clear button after clearing
      if (this.clearButton) {
        this.clearButton.style.display = 'none';
      }
    });

    this.form?.addEventListener('submit', this.onFormSubmit.bind(this));

    this.addEventListener('keydown', this.onKeydown.bind(this));

    document.addEventListener('click', this.onClickOutside.bind(this));
  }

  onChange() {
    const searchTerm = this.input.value.trim();
    this.searchTerm = searchTerm;

    // Show/hide clear button based on input value
    if (this.clearButton) {
      this.clearButton.style.display = searchTerm.length > 0 ? 'block' : 'none';
    }

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.input.value.trim().length) {
      event.preventDefault();
      return;
    }
  }

  onKeydown(event) {
    if (!this.isOpen()) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusNextResult();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousResult();
        break;
      case 'Escape':
        this.close();
        this.input.focus();
        break;
      case 'Enter':
        if (document.activeElement !== this.input) {
          event.preventDefault();
          document.activeElement.click();
        }
        break;
    }
  }

  onClickOutside(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  getSearchResults(searchTerm) {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    this.showLoading();

    fetch(
      `${window.Shopify?.routes?.root || '/'}search/suggest?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search&resources[type]=product,collection,article,page,query&resources[limit]=10&resources[options][unavailable_products]=last`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const resultsMarkup = html.querySelector('#predictive-search-results');

        if (resultsMarkup) {
          this.renderResults(resultsMarkup.outerHTML);
        } else {
          this.close();
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }
        this.close();
        console.error('Predictive search error:', error);
      });
  }

  renderResults(resultsMarkup) {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.open();
    }
  }

  showLoading() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = `
        <div class="predictive-search__loading">
          <span class="predictive-search__loading-spinner"></span>
        </div>
      `;
      this.open();
    }
  }

  open() {
    this.setAttribute('open', '');
    this.input?.setAttribute('aria-expanded', 'true');
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = 'block';
    }
  }

  close() {
    this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', 'false');
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = 'none';
      this.predictiveSearchResults.innerHTML = '';
    }
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  focusNextResult() {
    const results = this.getResultLinks();
    if (!results.length) return;

    const currentIndex = results.indexOf(document.activeElement);
    const nextIndex = currentIndex + 1 >= results.length ? 0 : currentIndex + 1;
    results[nextIndex].focus();
  }

  focusPreviousResult() {
    const results = this.getResultLinks();
    if (!results.length) return;

    const currentIndex = results.indexOf(document.activeElement);
    const previousIndex = currentIndex <= 0 ? results.length - 1 : currentIndex - 1;
    results[previousIndex].focus();
  }

  getResultLinks() {
    return Array.from(this.querySelectorAll('.predictive-search__link'));
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);
